import React, { useState, useEffect } from "react";
import {
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Label,
  Button,
} from "reactstrap";
import PackageTypeServices from "../../../services/Inventory Services/PackageTypeServices";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik, Form, Field, useFormik } from "formik";
import * as Yup from "yup"; // For validation schema
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Payment type name is required"),
  description: Yup.string().required("Description is required"),
  accountNumber: Yup.number()
    .min(0, "Account number must be greater than or equal to 0")
    .required("Account number is required"),
});

const EditModal = ({
  modal,
  toggleModal,
  editingPackage,
  savePackage,
  fetchPaymentType,
}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setSubmitting,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: editingPackage?.id || null,
      name: editingPackage?.name || "",
      description: editingPackage?.description || "",
      accountNumber: editingPackage?.accountNumber || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          name: values.name,
          description: values.description || "",
          accountNumber: values.accountNumber || "",
          isActive: true,
        };

        if (values?.id) {
          await PaymentTypeServices.update(values.id, payload);
          savePackage(payload);
          toast.success("Payment type updated successfully");
        } else {
          await PaymentTypeServices.create(payload);
          toast.success("Payment type created successfully");
          resetForm();
        }

        fetchPaymentType();
        toggleModal();
      } catch (error) {
        console.error("Error during package update:", error);
        toast.error(
          "Error updating package: " + (error.message || "Unknown error")
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal isOpen={modal} toggle={toggleModal} backdrop="static">
      <ModalHeader toggle={toggleModal}>
        {editingPackage?.id ? "Update" : "Create"} Payment Type
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <Row>
            <Col lg={12} className="mb-4">
              <div className="form-floating">
                <Input
                  type="text"
                  name="name"
                  id="name"
                  placeholder="Title"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-control ${
                    touched.name && errors.name ? "is-invalid" : ""
                  }`}
                />
                <Label for="name">Payment type name</Label>
                {touched.name && errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </Col>

            <Col lg={12} className="mb-3">
              <div className="form-floating">
                <Input
                  type="text"
                  name="accountNumber"
                  id="accountNumber"
                  placeholder="Account Number"
                  value={values.accountNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`form-control ${
                    touched.accountNumber && errors.accountNumber
                      ? "is-invalid"
                      : ""
                  }`}
                />
                <Label for="accountNumber">Account Number</Label>
                {touched.accountNumber && errors.accountNumber && (
                  <div className="invalid-feedback">{errors.accountNumber}</div>
                )}
              </div>
            </Col>

            <Col lg={12} className="mb-3">
              <div className="mb-3">
                <label className="form-label" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  className={`form-control ${
                    touched.description && errors.description
                      ? "is-invalid"
                      : ""
                  }`}
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.description && errors.description && (
                  <div className="invalid-feedback">
                    {errors.description}
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={toggleModal}>
            Cancel
          </Button>
          <Button type="submit" color="success" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditModal;
